import { useState, useMemo, useEffect } from 'react'
import { Inbox as InboxIcon, User, Megaphone, Building2, Bell, CheckCircle, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Email } from '../types/email'
import { Vendor } from '../types/vendor'
import { getInboxEmails } from '../data/mockEmails'
import { getEmailsFromCache, Email as CachedEmail, markEmailAsRead, getClearedEmails } from '../services/emailService'
import { getVendors } from '../services/vendorService'
import { getUserSettings } from '../services/userService'
import { useAuth } from '../contexts/AuthContext'

const EMAILS_PER_PAGE = 50
import EmailList from '../components/Email/EmailList'
import EmailView from '../components/Email/EmailView'
import VendorsTab from '../components/AIInbox/VendorsTab'
import PromotionsTab from '../components/AIInbox/PromotionsTab'
import UpdatesTab from '../components/AIInbox/UpdatesTab'
import ClearedTab from '../components/AIInbox/ClearedTab'

type InboxTab = 'all' | 'human' | 'promotions' | 'updates' | 'vendors' | 'cleared'

interface InboxProps {
  searchQuery: string
  refreshTrigger?: number
}

// ============================================================================
// HUMAN CLASSIFICATION SYSTEM
// ============================================================================

interface ClassificationResult {
  category: 'human' | 'promotions' | 'updates'
  subCategory?: 'transactional' | 'deal' | 'newsletter' | 'social' | 'other'
  confidence: number
  score: number
  signals: string[]
  vendorId?: string
  vendorName?: string
  isVendorVip: boolean
}

// Personal email domains - strong human signal
const PERSONAL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
  'aol.com', 'protonmail.com', 'proton.me', 'me.com', 'mac.com',
  'live.com', 'msn.com', 'ymail.com', 'googlemail.com'
]

// Automated sender patterns - strong automation signal
const AUTOMATED_SENDER_PATTERNS = [
  'noreply', 'no-reply', 'donotreply', 'do-not-reply',
  'newsletter', 'newsletters',
  'marketing', 'promo', 'promotions',
  'notifications', 'notification', 'notify',
  'alerts', 'alert',
  'updates', 'update',
  'news@', 'info@', 'hello@', 'team@', 'sales@',
  'support@', 'help@', 'contact@',
  'mailer-daemon', 'postmaster',
  'bounce', 'automated', 'auto-reply',
  'deals', 'offers', 'discount'
]

// Marketing call-to-action phrases
const MARKETING_CTAS = [
  'shop now', 'buy now', 'order now', 'get started',
  'claim offer', 'claim your', 'redeem now', 'redeem your',
  'learn more', 'find out more', 'discover more',
  'sign up', 'subscribe now', 'join now',
  'get offer', 'get deal', 'save now', 'save today',
  'limited time', 'act now', 'don\'t miss',
  'free shipping', 'free delivery',
  'click here', 'tap here'
]

// Bulk email footer indicators
const BULK_EMAIL_INDICATORS = [
  'unsubscribe', 'opt out', 'opt-out',
  'manage preferences', 'email preferences', 'update preferences',
  'view in browser', 'view online', 'view this email', 'view in your browser',
  'you are receiving this', 'you received this', 'this email was sent to',
  'to stop receiving', 'if you no longer wish',
  'privacy policy', 'terms of service',
  'sent by', 'powered by mailchimp', 'powered by sendgrid', 'powered by constant contact',
  'add us to your address book',
  '© 20', '©20' // Copyright notices typical in marketing emails
]

// Conversational patterns - human signals
const CONVERSATIONAL_PATTERNS = [
  // Questions
  /\?\s*$/m, // Ends with question mark
  /can you\b/i, /could you\b/i, /would you\b/i, /will you\b/i,
  /do you\b/i, /are you\b/i, /have you\b/i,
  /what do you think/i, /let me know/i, /thoughts\?/i,

  // First person addressing second person
  /\bi wanted to\b/i, /\bi\'m writing\b/i, /\bi thought\b/i,
  /\bi need\b/i, /\bi was wondering\b/i, /\bi hope\b/i,
  /\bjust wanted to\b/i, /\bwanted to check\b/i,
  /\bquick question\b/i, /\bhave a question\b/i,

  // Direct address
  /you mentioned/i, /your email/i, /as we discussed/i,
  /as you know/i, /per your request/i, /following up/i,
  /checking in/i, /touching base/i
]

// Personal sign-off patterns
const SIGNOFF_PATTERNS = [
  /\b(thanks|thank you|thx),?\s*\n/i,
  /\b(best|best regards|kind regards|regards|warm regards),?\s*\n/i,
  /\b(cheers|sincerely|yours truly|take care),?\s*\n/i,
  /\bsent from my (iphone|ipad|android|phone|mobile)/i,
  /\bget outlook for/i
]

// Check if email is from a personal domain
function checkPersonalDomain(email: string): { score: number; signal?: string } {
  const domain = email.toLowerCase().split('@')[1]
  if (PERSONAL_DOMAINS.includes(domain)) {
    return { score: 30, signal: 'personal_domain' }
  }
  return { score: 0 }
}

// Check if email is a reply
function checkIsReply(subject: string): { score: number; signal?: string } {
  const replyPattern = /^(re:|fw:|fwd:|aw:|wg:)\s*/i
  if (replyPattern.test(subject.trim())) {
    return { score: 25, signal: 'is_reply' }
  }
  return { score: 0 }
}

// Check for automated sender patterns
function checkAutomatedSender(email: string): { score: number; signals: string[] } {
  const lowerEmail = email.toLowerCase()
  const signals: string[] = []
  let score = 0

  for (const pattern of AUTOMATED_SENDER_PATTERNS) {
    if (lowerEmail.includes(pattern)) {
      signals.push(`automated_sender:${pattern}`)
      score -= 50
      break // One match is enough
    }
  }

  // Check for UUID/hash patterns in email (e.g., user+abc123def@, 12345.67890@)
  const hashPattern = /[a-f0-9]{8,}|[\d]{5,}\.[0-9]+@|\+[a-z0-9]+@/i
  if (hashPattern.test(lowerEmail)) {
    signals.push('hash_in_email')
    score -= 30
  }

  return { score, signals }
}

// Check for unsubscribe and bulk email indicators in content
function checkBulkEmailIndicators(body: string, htmlBody?: string): { score: number; signals: string[] } {
  const content = ((body || '') + ' ' + (htmlBody || '')).toLowerCase()
  const signals: string[] = []
  let score = 0

  for (const indicator of BULK_EMAIL_INDICATORS) {
    if (content.includes(indicator)) {
      // Unsubscribe is the strongest signal
      if (indicator === 'unsubscribe') {
        signals.push('has_unsubscribe')
        score -= 50
      } else if (indicator.includes('view in browser') || indicator.includes('view online')) {
        signals.push('has_view_in_browser')
        score -= 40
      } else if (indicator.includes('privacy policy') || indicator.includes('terms of service')) {
        signals.push('has_legal_links')
        score -= 25
      } else if (indicator.includes('you are receiving') || indicator.includes('this email was sent')) {
        signals.push('has_bulk_footer')
        score -= 35
      } else {
        signals.push(`bulk_indicator:${indicator.substring(0, 20)}`)
        score -= 20
      }

      // Cap the penalty from bulk indicators
      if (score <= -100) break
    }
  }

  return { score: Math.max(score, -100), signals }
}

// Check for marketing CTAs
function checkMarketingCTAs(body: string, htmlBody?: string): { score: number; signals: string[] } {
  const content = ((body || '') + ' ' + (htmlBody || '')).toLowerCase()
  const signals: string[] = []
  let matchCount = 0

  for (const cta of MARKETING_CTAS) {
    if (content.includes(cta)) {
      matchCount++
      if (matchCount <= 3) {
        signals.push(`marketing_cta:${cta}`)
      }
    }
  }

  // Score based on how many CTAs found
  const score = matchCount > 0 ? -15 * Math.min(matchCount, 4) : 0

  return { score, signals }
}

// Check for conversational patterns (human signals)
function checkConversationalPatterns(body: string): { score: number; signals: string[] } {
  const signals: string[] = []
  let matchCount = 0

  for (const pattern of CONVERSATIONAL_PATTERNS) {
    if (pattern.test(body)) {
      matchCount++
      if (matchCount <= 3) {
        signals.push('conversational_tone')
      }
    }
  }

  // Score based on matches
  const score = matchCount > 0 ? 10 * Math.min(matchCount, 4) : 0

  return { score, signals: matchCount > 0 ? ['conversational_tone'] : [] }
}

// Check for personal sign-offs
function checkPersonalSignoff(body: string): { score: number; signal?: string } {
  for (const pattern of SIGNOFF_PATTERNS) {
    if (pattern.test(body)) {
      return { score: 15, signal: 'personal_signoff' }
    }
  }
  return { score: 0 }
}

// Check email length (short emails more likely human)
function checkEmailLength(body: string): { score: number; signal?: string } {
  const length = body.length

  if (length < 300) {
    return { score: 15, signal: 'short_email' }
  } else if (length < 800) {
    return { score: 5, signal: 'moderate_length' }
  } else if (length > 3000) {
    return { score: -10, signal: 'very_long_email' }
  }

  return { score: 0 }
}

// Check against known vendors
function checkKnownVendor(email: string, vendors: Vendor[]): { score: number; signal?: string; vendor?: Vendor } {
  const lowerEmail = email.toLowerCase()

  const matchedVendor = vendors.find(v =>
    lowerEmail.includes(v.domain) || v.senderEmails.some(se => lowerEmail === se.toLowerCase())
  )

  if (matchedVendor) {
    if (matchedVendor.isVip) {
      return { score: 100, signal: 'vendor_vip', vendor: matchedVendor }
    }
    return { score: -40, signal: 'known_vendor', vendor: matchedVendor }
  }

  return { score: 0 }
}

// Main classification function
function classifyEmail(
  email: { from: string; fromName?: string; subject: string; body: string; htmlBody?: string },
  vendors: Vendor[]
): ClassificationResult {
  const signals: string[] = []
  let totalScore = 0

  // 1. Check personal domain (+30)
  const domainResult = checkPersonalDomain(email.from)
  totalScore += domainResult.score
  if (domainResult.signal) signals.push(domainResult.signal)

  // 2. Check if reply (+25)
  const replyResult = checkIsReply(email.subject)
  totalScore += replyResult.score
  if (replyResult.signal) signals.push(replyResult.signal)

  // 3. Check automated sender patterns (-50)
  const automatedResult = checkAutomatedSender(email.from)
  totalScore += automatedResult.score
  signals.push(...automatedResult.signals)

  // 4. Check bulk email indicators (unsubscribe, view in browser, etc.)
  const bulkResult = checkBulkEmailIndicators(email.body, email.htmlBody)
  totalScore += bulkResult.score
  signals.push(...bulkResult.signals)

  // 5. Check marketing CTAs
  const ctaResult = checkMarketingCTAs(email.body, email.htmlBody)
  totalScore += ctaResult.score
  signals.push(...ctaResult.signals)

  // 6. Check conversational patterns (+10-40)
  const conversationalResult = checkConversationalPatterns(email.body)
  totalScore += conversationalResult.score
  signals.push(...conversationalResult.signals)

  // 7. Check personal sign-off (+15)
  const signoffResult = checkPersonalSignoff(email.body)
  totalScore += signoffResult.score
  if (signoffResult.signal) signals.push(signoffResult.signal)

  // 8. Check email length
  const lengthResult = checkEmailLength(email.body)
  totalScore += lengthResult.score
  if (lengthResult.signal) signals.push(lengthResult.signal)

  // 9. Check known vendors (can override everything if VIP or marked as human)
  const vendorResult = checkKnownVendor(email.from, vendors)

  // If vendor is explicitly marked as human, always classify as human
  if (vendorResult.vendor?.isHuman) {
    return {
      category: 'human',
      confidence: 1.0,
      score: 100,
      signals: ['vendor_marked_human'],
      vendorId: vendorResult.vendor.id,
      vendorName: vendorResult.vendor.name,
      isVendorVip: vendorResult.vendor.isVip || false
    }
  }

  // VIP vendor - always human
  if (vendorResult.vendor?.isVip) {
    return {
      category: 'human',
      confidence: 1.0,
      score: 100,
      signals: ['vendor_vip'],
      vendorId: vendorResult.vendor.id,
      vendorName: vendorResult.vendor.name,
      isVendorVip: true
    }
  }
  totalScore += vendorResult.score
  if (vendorResult.signal) signals.push(vendorResult.signal)

  // Determine category based on score
  // Score >= 40: Human
  // Score <= -30: Promotional
  // In between: Default to promotional (conservative)

  let category: 'human' | 'promotions' | 'updates' = 'promotions'
  let confidence = 0.5

  if (totalScore >= 40) {
    category = 'human'
    confidence = Math.min(0.95, 0.6 + (totalScore - 40) / 100)
  } else if (totalScore <= -30) {
    category = 'promotions'
    confidence = Math.min(0.95, 0.6 + Math.abs(totalScore + 30) / 100)
  } else {
    // Uncertain - default to promotional but with low confidence
    category = 'promotions'
    confidence = 0.4
  }

  return {
    category,
    confidence,
    score: totalScore,
    signals,
    vendorId: vendorResult.vendor?.id,
    vendorName: vendorResult.vendor?.name,
    isVendorVip: false
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

// Convert cached email to app email format
function convertCachedEmail(cached: CachedEmail, vendors: Vendor[]): Email {
  return {
    id: cached.id,
    accountId: cached.accountId,
    accountEmail: cached.accountEmail,
    accountColor: cached.accountColor,
    from: {
      name: cached.fromName || cached.from,
      email: cached.from
    },
    to: [{
      name: cached.to,
      email: cached.to
    }],
    subject: cached.subject,
    body: cached.body,
    htmlBody: cached.htmlBody,
    preview: cached.snippet || cached.body.substring(0, 100),
    date: cached.date instanceof Date ? cached.date : new Date(cached.date),
    isRead: cached.isRead,
    isStarred: false,
    folder: cached.folder as Email['folder'],
    // Pass through vendor info from Firestore
    vendorId: cached.vendorId,
    vendorName: cached.vendorName,
    vendorDomain: cached.vendorDomain,
    aiClassification: classifyEmail(
      {
        from: cached.from,
        fromName: cached.fromName,
        subject: cached.subject,
        body: cached.body,
        htmlBody: cached.htmlBody || undefined
      },
      vendors
    )
  }
}

export default function Inbox({ searchQuery, refreshTrigger = 0 }: InboxProps) {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<InboxTab>('all')
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null)
  const [cachedEmails, setCachedEmails] = useState<Email[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [updatesCount, setUpdatesCount] = useState(0)
  const [clearedCount, setClearedCount] = useState(0)
  const [mobileShowEmail, setMobileShowEmail] = useState(false)

  // Load updates and cleared counts
  const loadTabCounts = async (userId: string) => {
    try {
      // Get updates count (non-dismissed)
      const updatesRef = collection(db, 'users', userId, 'updates')
      const updatesQuery = query(updatesRef, where('isDismissed', '==', false))
      const updatesSnapshot = await getDocs(updatesQuery)
      setUpdatesCount(updatesSnapshot.size)

      // Get cleared emails count
      const clearedResult = await getClearedEmails(500)
      setClearedCount(clearedResult.count || 0)
    } catch (error) {
      console.error('Failed to load tab counts:', error)
    }
  }

  // Fetch emails from cache when component mounts or refresh is triggered
  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        setCachedEmails([])
        setVendors([])
        setUpdatesCount(0)
        setClearedCount(0)
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        // Get user settings for retention period
        const settings = await getUserSettings(user.uid)
        const retention = settings?.emailRetentionDays || 30

        // Fetch more emails to cover retention period (estimate ~20 emails/day max)
        const maxEmails = retention * 20
        const [fetchedEmails, fetchedVendors] = await Promise.all([
          getEmailsFromCache(user.uid, 'INBOX', maxEmails),
          getVendors(user.uid)
        ])

        // Filter emails by retention period
        const cutoffDate = new Date()
        cutoffDate.setDate(cutoffDate.getDate() - retention)

        const filteredEmails = fetchedEmails.filter(e => {
          const emailDate = e.date instanceof Date ? e.date : new Date(e.date)
          return emailDate >= cutoffDate
        })

        setCachedEmails(filteredEmails.map(e => convertCachedEmail(e, fetchedVendors)))
        setVendors(fetchedVendors)

        // Load tab counts
        await loadTabCounts(user.uid)
      } catch (error) {
        console.error('Failed to load emails from cache:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [user, refreshTrigger])

  // Reset to page 1 when tab changes or search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, searchQuery])

  // Mark email as read when selected
  useEffect(() => {
    if (selectedEmail && !selectedEmail.isRead && user) {
      // Mark as read in Firestore
      markEmailAsRead(user.uid, selectedEmail.id).catch(err => {
        console.error('Failed to mark email as read:', err)
      })

      // Update local state to show as read
      setCachedEmails(prev => prev.map(e =>
        e.id === selectedEmail.id ? { ...e, isRead: true } : e
      ))
      setSelectedEmail(prev => prev ? { ...prev, isRead: true } : null)
    }
  }, [selectedEmail?.id, user])

  // All emails (for "All" tab)
  const allEmails = useMemo(() => {
    const emails = cachedEmails.length > 0 ? cachedEmails : getInboxEmails()

    if (!searchQuery.trim()) return emails

    const query = searchQuery.toLowerCase()
    return emails.filter(
      (email) =>
        email.subject.toLowerCase().includes(query) ||
        email.from.name.toLowerCase().includes(query) ||
        email.from.email.toLowerCase().includes(query) ||
        email.preview.toLowerCase().includes(query)
    )
  }, [searchQuery, cachedEmails])

  // Human emails
  const humanEmails = useMemo(() => {
    let result = cachedEmails.filter(e => e.aiClassification?.category === 'human')

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(e =>
        e.subject.toLowerCase().includes(query) ||
        e.from.name.toLowerCase().includes(query) ||
        e.from.email.toLowerCase().includes(query) ||
        e.preview.toLowerCase().includes(query)
      )
    }

    return result
  }, [cachedEmails, searchQuery])

  // Counts for tabs
  const counts = useMemo(() => ({
    all: allEmails.length,
    human: cachedEmails.filter(e => e.aiClassification?.category === 'human').length,
    promotions: cachedEmails.filter(e => e.aiClassification?.category === 'promotions').length
  }), [allEmails, cachedEmails])

  const handleVendorUpdate = () => {
    if (user) {
      getVendors(user.uid).then(setVendors)
    }
  }

  const handleRefresh = async () => {
    if (!user) return

    try {
      // Get user settings for retention period (same as initial load)
      const settings = await getUserSettings(user.uid)
      const retention = settings?.emailRetentionDays || 30
      const maxEmails = retention * 20

      const [fetchedEmails, fetchedVendors] = await Promise.all([
        getEmailsFromCache(user.uid, 'INBOX', maxEmails),
        getVendors(user.uid)
      ])

      // Filter emails by retention period
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - retention)

      const filteredEmails = fetchedEmails.filter(e => {
        const emailDate = e.date instanceof Date ? e.date : new Date(e.date)
        return emailDate >= cutoffDate
      })

      setCachedEmails(filteredEmails.map(e => convertCachedEmail(e, fetchedVendors)))
      setVendors(fetchedVendors)

      // Reload tab counts
      await loadTabCounts(user.uid)
    } catch (error) {
      console.error('Failed to refresh emails:', error)
    }
  }

  // Get the emails to display based on active tab (with pagination)
  const filteredEmails = activeTab === 'all' ? allEmails : humanEmails
  const totalPages = Math.ceil(filteredEmails.length / EMAILS_PER_PAGE)
  const startIndex = (currentPage - 1) * EMAILS_PER_PAGE
  const displayEmails = filteredEmails.slice(startIndex, startIndex + EMAILS_PER_PAGE)

  // Create a vendor map for looking up auto-delete settings
  const vendorMap = useMemo(() => {
    const map = new Map<string, Vendor>()
    for (const vendor of vendors) {
      if (vendor.id) {
        map.set(vendor.id, vendor)
      }
    }
    return map
  }, [vendors])

  // Determine if the selected email's sender is human or vendor
  const getSelectedEmailSenderStatus = (): boolean | undefined => {
    if (!selectedEmail) return undefined

    // Extract sender email
    const senderEmail = selectedEmail.from.email.toLowerCase()

    // Check if we have a vendor record for this sender
    const matchedVendor = vendors.find(v =>
      senderEmail.includes(v.domain) || v.senderEmails?.some(se => senderEmail === se.toLowerCase())
    )

    // If vendor exists and has explicit isHuman flag, use it
    if (matchedVendor && matchedVendor.isHuman !== undefined) {
      return matchedVendor.isHuman
    }

    // Otherwise, use the AI classification
    if (selectedEmail.aiClassification?.category === 'human') {
      return true
    } else if (selectedEmail.aiClassification?.category === 'promotions' || selectedEmail.aiClassification?.category === 'updates') {
      return false
    }

    return undefined
  }

  const selectedEmailSenderIsHuman = getSelectedEmailSenderStatus()

  // Handle email selection (with mobile view support)
  const handleSelectEmail = (email: Email) => {
    setSelectedEmail(email)
    setMobileShowEmail(true)
  }

  // Handle back button on mobile
  const handleMobileBack = () => {
    setMobileShowEmail(false)
  }

  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null

    const pages: (number | string)[] = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages)
      }
    }

    return (
      <div className="flex items-center justify-center gap-1 py-3 px-4 border-t border-gray-200 bg-gray-50">
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={18} />
        </button>

        {pages.map((page, idx) => (
          typeof page === 'number' ? (
            <button
              key={idx}
              onClick={() => setCurrentPage(page)}
              className={`min-w-[32px] h-8 px-2 text-sm font-medium rounded transition-colors ${
                currentPage === page
                  ? 'bg-primary-600 text-white'
                  : 'hover:bg-gray-200 text-gray-700'
              }`}
            >
              {page}
            </button>
          ) : (
            <span key={idx} className="px-1 text-gray-400">...</span>
          )
        ))}

        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Tab Bar - horizontally scrollable on mobile */}
      <div className="bg-white border-b border-gray-200 px-2 lg:px-4">
        <div className="flex gap-1 overflow-x-auto scrollbar-hide -mx-2 px-2 lg:mx-0 lg:px-0">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-shrink-0 flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'all'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <InboxIcon size={18} />
            <span className="hidden sm:inline">All Mail</span>
            <span className="sm:hidden">All</span>
            {counts.all > 0 && (
              <span className={`px-1.5 lg:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'all' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {counts.all}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('human')}
            className={`flex-shrink-0 flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'human'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <User size={18} />
            Human
            {counts.human > 0 && (
              <span className={`px-1.5 lg:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'human' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {counts.human}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('promotions')}
            className={`flex-shrink-0 flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'promotions'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Megaphone size={18} />
            <span className="hidden sm:inline">Promotions</span>
            <span className="sm:hidden">Promo</span>
            {counts.promotions > 0 && (
              <span className={`px-1.5 lg:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'promotions' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {counts.promotions}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('updates')}
            className={`flex-shrink-0 flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'updates'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bell size={18} />
            Updates
            {updatesCount > 0 && (
              <span className={`px-1.5 lg:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'updates' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {updatesCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('vendors')}
            className={`flex-shrink-0 flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'vendors'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Building2 size={18} />
            Vendors
            {vendors.length > 0 && (
              <span className={`px-1.5 lg:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'vendors' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {vendors.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('cleared')}
            className={`flex-shrink-0 flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'cleared'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <CheckCircle size={18} />
            Cleared
            {clearedCount > 0 && (
              <span className={`px-1.5 lg:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'cleared' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {clearedCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'vendors' ? (
        <VendorsTab
          vendors={vendors}
          onVendorUpdate={handleVendorUpdate}
          loading={loading}
        />
      ) : activeTab === 'promotions' && user ? (
        <PromotionsTab userId={user.uid} onRefresh={handleRefresh} />
      ) : activeTab === 'updates' && user ? (
        <UpdatesTab userId={user.uid} onRefresh={handleRefresh} />
      ) : activeTab === 'cleared' && user ? (
        <ClearedTab userId={user.uid} onRefresh={handleRefresh} />
      ) : (
        <div className="flex-1 flex overflow-hidden relative">
          {/* Email List - full width on mobile, fixed width on desktop */}
          <div className={`w-full lg:w-96 border-r border-gray-200 flex flex-col bg-white ${
            mobileShowEmail ? 'hidden lg:flex' : 'flex'
          }`}>
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {activeTab === 'all' ? 'Inbox' : 'Human'}
              </h2>
              <p className="text-sm text-gray-500">
                {loading ? 'Loading...' : (
                  <>
                    {filteredEmails.length} {filteredEmails.length === 1 ? 'message' : 'messages'}
                    {totalPages > 1 && (
                      <span className="text-gray-400"> - Page {currentPage} of {totalPages}</span>
                    )}
                  </>
                )}
              </p>
            </div>
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredEmails.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-8">
                {activeTab === 'human' ? (
                  <>
                    <User size={48} className="mb-4 opacity-50" />
                    <p className="text-center">No emails from humans yet.</p>
                    <p className="text-sm text-center mt-2 opacity-75">
                      Emails from people you've corresponded with will appear here.
                    </p>
                  </>
                ) : (
                  <>
                    <InboxIcon size={48} className="mb-4 opacity-50" />
                    <p className="text-center">No emails found.</p>
                  </>
                )}
              </div>
            ) : (
              <>
                <EmailList
                  emails={displayEmails}
                  selectedEmailId={selectedEmail?.id || null}
                  onSelectEmail={handleSelectEmail}
                  vendorMap={vendorMap}
                />
                <Pagination />
              </>
            )}
          </div>

          {/* Email View - hidden on mobile when no email selected, full screen overlay when selected */}
          <div className={`${
            mobileShowEmail
              ? 'fixed inset-0 z-50 bg-white lg:relative lg:inset-auto lg:z-auto'
              : 'hidden lg:block'
          } lg:flex-1`}>
            {/* Mobile back button */}
            {mobileShowEmail && (
              <div className="lg:hidden flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-white">
                <button
                  onClick={handleMobileBack}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 -ml-1 p-1"
                >
                  <ArrowLeft size={20} />
                  <span className="text-sm font-medium">Back to list</span>
                </button>
              </div>
            )}
            <div className={`${mobileShowEmail ? 'h-[calc(100%-53px)] lg:h-full' : 'h-full'}`}>
              <EmailView
                email={selectedEmail}
                userId={user?.uid}
                senderIsHuman={selectedEmailSenderIsHuman}
                onSenderStatusChange={handleRefresh}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
